<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18N } from '@dialpad/i18n';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type {
  Item,
  ItemGroup,
} from '@/app/components/SearchDropdown/SearchDropdown.vue';
import { DATA_QA } from './dnc-field-select.constants';
import logger from '@/logger';

const { $t } = useI18N();

type Props = {
  required?: boolean;
  label?: string;
  disabled?: boolean;
  fields: string[];
  row?: number;
};

const props = withDefaults(defineProps<Props>(), {
  required: false,
  label: '',
  disabled: false,
  fields: () => [],
  row: 0,
});

const log = computed(() => logger.getChild(`DncFieldSelect:${props.row}`));
log.value.debug('Mounting');

onMounted(() => {
  log.value.debug('Mounted');
});

const model = defineModel<string | null>({
  default: null,
});

const value = ref<Item<string> | null>(null);
const filter = ref('');

const fieldMap = (field: string): Item<string> => ({
  key: field,
  title: field,
  ref: field,
  icon: 'google-sheets',
});

const fieldFilter = (item: Item<string>) =>
  item.title.toLowerCase().includes(filter.value.toLowerCase());

const groups = computed(() => {
  return [
    {
      items: props.fields.map(fieldMap).filter(fieldFilter),
    } as ItemGroup<string>,
  ];
});

watch(
  model,
  (newValue) => {
    log.value.debug('Model changed', newValue);
    if (newValue === null) {
      value.value = null;
      return;
    }

    const allItems = groups.value.flatMap((group) => group.items);
    const foundItem = allItems.find((item) => item.ref === newValue);
    value.value = foundItem ?? null;
  },
  { immediate: true },
);

watch([value], () => {
  if (model.value === value.value?.ref) return;
  log.value.debug('Updating model', value.value);
  model.value = value.value?.ref ?? null;
});

const placeholder = computed(() => {
  if (props.disabled) {
    return $t('DNC_FIELD_SELECT_DISABLED_PLACEHOLDER');
  }
  return $t('DNC_FIELD_SELECT_PLACEHOLDER');
});

const errorMessages = computed(() => ({
  REQUIRED: $t('DNC_FIELD_SELECT_REQUIRED'),
}));

const messages = computed(() => ({
  NO_RESULTS: $t('DNC_FIELD_SELECT_NO_RESULTS'),
  ERROR_LOADING_RESULTS: $t('DNC_FIELD_SELECT_NO_RESULTS'),
}));
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="value"
      :label="label"
      :messages="messages"
      :list-aria-label="$t('DNC_FIELD_SELECT_ARIA_LABEL')"
      :groups="groups"
      :debounce="0"
      :required="required"
      :disabled="disabled"
      :show-list-icons="true"
      :placeholder="placeholder"
      :error-messages="errorMessages"
      @update:search="(value) => (filter = value)"
    />
  </div>
</template>
